/**
 * EMSPCR first-aid cross badge — square red tile with a thick white
 * medical plus. Used in the login screen and app shell header.
 *
 * Why not a lucide icon: lucide's `Plus` is too thin and `Cross` is the
 * religious shape. A proper first-aid cross needs thick arms that fill
 * the square, which is a custom glyph.
 *
 * Dual-mode:
 *   - cockpitV2 OFF (default): flat lacquered red square with the
 *     existing cross. Looks identical to today.
 *   - cockpitV2 ON: layered "etched-metal" rendering — radial light-
 *     fall on the tile face, beveled bezel via gradient borders, the
 *     cross gets a subtle inner highlight + bottom shadow so it reads
 *     as engraved white-enamel into the red metal surface. The
 *     heartbeat dot in the wordmark next to it is unchanged (still
 *     wordmark-pulse). Same dimensions as v1 so it slots into existing
 *     layouts without repositioning.
 */
import { useThemeStore } from '@/lib/theme-store';

interface BrandmarkProps {
  /** Size of the outer square in px. Default 32. */
  size?: number;
  className?: string;
}

export function Brandmark({ size = 32, className = '' }: BrandmarkProps) {
  const cockpitV2 = useThemeStore((s) => s.cockpitV2);

  if (!cockpitV2) {
    // Legacy v1 — flat red tile + simple cross.
    const inner = Math.round(size * 0.6);
    return (
      <div
        className={
          'flex items-center justify-center rounded-sm bg-ems-red border border-red-400/60 shadow-sm ' +
          className
        }
        style={{ width: size, height: size }}
      >
        <svg
          width={inner}
          height={inner}
          viewBox="0 0 24 24"
          fill="currentColor"
          className="text-white"
          aria-hidden="true"
        >
          <rect x="9.5" y="3" width="5" height="18" rx="0.75" />
          <rect x="3" y="9.5" width="18" height="5" rx="0.75" />
        </svg>
      </div>
    );
  }

  // cockpit-v2 — layered etched-metal rendering.
  const stroke = Math.max(1, Math.round(size * 0.04));
  return (
    <div
      className={'flex items-center justify-center shrink-0 ' + className}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        <defs>
          {/* Tile face — radial light-fall: hot red top-left,
              shading toward darker red at bottom-right. Reads as
              powder-coated apparatus red catching cabin light. */}
          <radialGradient id="bm-face" cx="35%" cy="30%" r="80%">
            <stop offset="0%" stopColor="#e84545" />
            <stop offset="55%" stopColor="#c42424" />
            <stop offset="100%" stopColor="#7d1111" />
          </radialGradient>
          {/* Cross face — subtle off-white with vertical sheen so it
              reads as enameled, not flat painted. */}
          <linearGradient id="bm-cross" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#e8eaee" />
          </linearGradient>
          {/* Bezel rim — bright top-left + dark bottom-right edges
              for the raised-metal cue. */}
          <linearGradient id="bm-rim" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
            <stop offset="50%" stopColor="rgba(0,0,0,0.05)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.45)" />
          </linearGradient>
        </defs>

        {/* Outer bezel — thin metal frame around the tile */}
        <rect
          x="2" y="2" width="96" height="96" rx="6" ry="6"
          fill="url(#bm-rim)"
        />
        {/* Inset tile face */}
        <rect
          x="5" y="5" width="90" height="90" rx="4" ry="4"
          fill="url(#bm-face)"
          stroke="rgba(0,0,0,0.45)" strokeWidth={stroke}
        />
        {/* Cross — vertical bar */}
        <rect
          x="38" y="18" width="24" height="64" rx="2" ry="2"
          fill="url(#bm-cross)"
        />
        {/* Cross — horizontal bar */}
        <rect
          x="18" y="38" width="64" height="24" rx="2" ry="2"
          fill="url(#bm-cross)"
        />
        {/* Cross top-edge highlight (1px, white at 35% alpha) — reads
            as light catching the lifted enamel edge */}
        <path
          d="M 38 18 L 62 18 M 18 38 L 38 38 M 62 38 L 82 38"
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="0.8" fill="none"
        />
        {/* Cross bottom-edge shadow (1px, black at 35% alpha) — reads
            as the depth where the cross meets the red enamel */}
        <path
          d="M 38 82 L 62 82 M 18 62 L 38 62 M 62 62 L 82 62"
          stroke="rgba(0,0,0,0.4)"
          strokeWidth="0.8" fill="none"
        />
      </svg>
    </div>
  );
}
